import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useTeamWorkload } from '../../hooks/useTeamWorkload';
import { TeamWorkloadRow } from './TeamWorkloadRow';

function LegendDot({ colorClass, label }: { colorClass: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${colorClass}`} />
      {label}
    </span>
  );
}

export function TeamWorkloadList() {
  const { t } = useTranslation('analytics');
  const { data, isLoading } = useTeamWorkload();

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="analytics-team-workload-list">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-5 w-full rounded" />
        ))}
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">{t('empty.teamWorkload')}</p>
    );
  }

  return (
    <div className="space-y-4" data-testid="analytics-team-workload-list">
      {/* Stacked bar overview */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-4">
          <LegendDot colorClass="bg-blue-500" label={t('projectHealth.legend.done')} />
          <LegendDot colorClass="bg-cyan-400" label={t('projectHealth.legend.inProgress')} />
          <LegendDot colorClass="bg-gray-300" label={t('projectHealth.legend.todo')} />
          <LegendDot colorClass="bg-red-500" label={t('projectHealth.legend.overdue')} />
        </div>

        {data.map((member) => {
          const total = member.totalTasks || 1;
          const donePct = (member.doneTasks / total) * 100;
          const inProgressPct = (member.inProgressTasks / total) * 100;
          const todoPct = (member.todoTasks / total) * 100;
          const overduePct = (member.overdueTasks / total) * 100;

          return (
            <div key={member.userId ?? member.userName}>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-medium">{member.userName ?? '—'}</span>
                <span className="text-xs text-muted-foreground">
                  {t('projectHealth.taskCount', { count: member.totalTasks })}
                </span>
              </div>
              <div className="flex h-3 overflow-hidden rounded-full bg-secondary">
                {donePct > 0 && (
                  <div className="h-full bg-blue-500" style={{ width: `${donePct}%` }} />
                )}
                {inProgressPct > 0 && (
                  <div className="h-full bg-cyan-400" style={{ width: `${inProgressPct}%` }} />
                )}
                {todoPct > 0 && (
                  <div className="h-full bg-gray-300" style={{ width: `${todoPct}%` }} />
                )}
                {overduePct > 0 && (
                  <div className="h-full bg-red-500" style={{ width: `${overduePct}%` }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t" />

      <div className="space-y-2">
        {data.map((member) => (
          <TeamWorkloadRow key={member.userId ?? member.userName} member={member} />
        ))}
      </div>
    </div>
  );
}
